<template>
    <div class="body bank-app">
        <Navbar :show-back-button="true" @back="navigateToDashboard"/>

        <!-- Page Header -->
        <div class="section-header">
            <h3 class="section-title mt-3" style="margin-right: 10px;">
                {{ $t('employer.selectListTaf.title') || 'لیست کاربران' }}
            </h3>
        </div>

        <!-- Action Bar -->
        <div class="action-bar">
            <!-- Print -->
            <button class="print-btn" @click="printData" :title="$t('employer.selectListTaf.print') || 'چاپ'">
                🖨️
            </button>

            <!-- Search Input -->
            <div class="search-container">
                <input
                    type="text"
                    v-model="searchQuery"
                    :placeholder="$t('employer.selectListTaf.searchPlaceholder') || 'جستجو...'"
                    class="search-input"
                />
                <span class="search-icon">🔍</span>
            </div>

            <!-- Total Count -->
            <div class="total-count">
                <span>{{ $t('employer.selectListTaf.total') || 'تعداد کل' }}: {{ filteredData.length }}</span>
            </div>

            <!-- Select All Checkbox -->
            <div class="select-all-container">
                <label class="select-all-checkbox">
                    <input 
                        type="checkbox" 
                        :checked="allSelected"
                        @change="toggleSelectAll"
                    />
                    <span class="checkbox-label">{{ $t('employer.selectListTaf.selectAll') || 'انتخاب همه' }}</span>
                </label>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <span>{{ $t('employer.selectListTaf.loading') || 'در حال بارگذاری...' }}</span>
        </div>

        <!-- Error -->
        <div v-if="error" class="error-state">
            <div class="error-icon">⚠️</div>
            <h4>{{ $t('employer.selectListTaf.error') || 'خطا' }}</h4>
            <p>{{ error }}</p>
            <button class="retry-btn" @click="fetchUserList">{{ $t('employer.selectListTaf.retry') || 'تلاش مجدد' }}</button>
        </div>

        <!-- User Grid -->
        <div v-if="!loading && !error && filteredData.length > 0" class="user-section">
            <div class="user-grid">
                <div class="user-card" v-for="(user, index) in paginatedData" :key="user.Id_subcode || index">
                    <div class="card-header">
                        <div class="user-id">
                            <input 
                                type="checkbox" 
                                :checked="user._selected"
                                @change="toggleUserSelection(user)"
                                class="user-select-checkbox"
                            />
                            <span>#{{ user.Id_subcode }}</span>
                        </div>
                        <div class="user-role" :class="getRoleClass(user.Role)">
                            {{ getPersianRole(user.Role) }}
                        </div>
                    </div>

                    <div class="card-body">
                        <div class="user-info">
                            <div class="info-row">
                                <span class="info-label">{{ $t('employer.selectListTaf.name') || 'نام' }}:</span>
                                <span class="info-value">{{ user.title || '-' }}</span>
                            </div>

                            <div class="info-row">
                                <span class="info-label">{{ $t('employer.selectListTaf.phone') || 'تلفن' }}:</span>
                                <span class="info-value phone">{{ user.Tel2 || '-' }}</span>
                            </div>

                            <!-- <div class="info-row role-row">
                                <span class="info-label">{{ $t('employer.selectListTaf.role') || 'نقش' }}:</span>
                                <span class="info-value role-badge" :class="getRoleBadgeClass(user.Role)">
                                    {{ getPersianRole(user.Role) }}
                                </span>
                            </div> -->

                            <!-- Role Selection Checkboxes -->
                            <div class="info-row role-selection-row">
                                <span class="info-label">{{ $t('employer.selectListTaf.changeRole') || 'تغییر نقش' }}:</span>
                                <div class="role-checkboxes">
                                    <label 
                                        v-for="roleOption in availableRoles" 
                                        :key="roleOption.value"
                                        class="role-checkbox"
                                        :class="{ 'role-checkbox-selected': isRoleSelected(user, roleOption.value) }"
                                    >
                                        <input 
                                            type="checkbox" 
                                            :value="roleOption.value"
                                            :checked="isRoleSelected(user, roleOption.value)"
                                            @change="onRoleChange(user, roleOption.value)"
                                        />
                                        <span class="checkbox-label">{{ roleOption.label }}</span>
                                    </label>
                                </div>
                            </div>
                            <div class="submit-left">
                                <button 
                                    class="submit-changes-btn" 
                                    @click="submitRoleChanges"
                                    :disabled="!hasChanges || submitting"
                                >
                                    <span v-if="submitting" class="spinner"></span>
                                    <span v-else>💾</span>
                                    {{ submitting ? $t('employer.selectListTaf.submitting') || 'در حال ذخیره...' : $t('employer.selectListTaf.submitChanges') || 'ذخیره تغییرات' }}
                                </button>
                                <span v-if="hasChanges" class="changes-count">
                                    {{ $t('employer.selectListTaf.changesCount') || 'تعداد تغییرات' }}: {{ changedUsersCount }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div class="pagination-controls">
                <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
                    {{ $t('employer.selectListTaf.prev') || 'قبلی' }}
                </button>

                <span class="page-info">
                    {{ $t('employer.selectListTaf.page') || 'صفحه' }} {{ currentPage }}
                    {{ $t('employer.selectListTaf.of') || 'از' }}
                    {{ totalPages }}
                </span>

                <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">
                    {{ $t('employer.selectListTaf.next') || 'بعدی' }}
                </button>
            </div>

            <!-- Submit Changes Button -->
            <div class="submit-changes-container">
                <div class="submit-left">
                    <button 
                        class="submit-changes-btn" 
                        @click="submitRoleChanges"
                        :disabled="!hasChanges || submitting"
                    >
                        <span v-if="submitting" class="spinner"></span>
                        <span v-else>💾</span>
                        {{ submitting ? $t('employer.selectListTaf.submitting') || 'در حال ذخیره...' : $t('employer.selectListTaf.submitChanges') || 'ذخیره تغییرات' }}
                    </button>
                    <span v-if="hasChanges" class="changes-count">
                        {{ $t('employer.selectListTaf.changesCount') || 'تعداد تغییرات' }}: {{ changedUsersCount }}
                    </span>
                </div>
                
                <div class="submit-right">
                    <button 
                        class="reset-changes-btn" 
                        @click="resetChanges"
                        :disabled="!hasChanges || submitting"
                    >
                        🔄 {{ $t('employer.selectListTaf.resetChanges') || 'لغو تغییرات' }}
                    </button>
                </div>
            </div>

            <!-- Bulk Role Assignment -->
            <div v-if="selectedUsersCount > 0" class="bulk-assign-container">
                <span class="bulk-label">
                    {{ $t('employer.selectListTaf.bulkAssign') || 'اختصاص نقش به کاربران انتخاب شده' }} ({{ selectedUsersCount }}):
                </span>
                <div class="bulk-role-checkboxes">
                    <label 
                        v-for="roleOption in availableRoles" 
                        :key="roleOption.value"
                        class="bulk-role-checkbox"
                    >
                        <input 
                            type="checkbox" 
                            :value="roleOption.value"
                            v-model="bulkRoleSelection"
                        />
                        <span class="checkbox-label">{{ roleOption.label }}</span>
                    </label>
                </div>
                <button 
                    class="apply-bulk-btn" 
                    @click="applyBulkRoles"
                    :disabled="bulkRoleSelection.length === 0 || submitting"
                >
                    {{ $t('employer.selectListTaf.applyBulk') || 'اعمال به انتخاب‌شده‌ها' }}
                </button>
            </div>
        </div>

        <!-- No Results -->
        <div v-if="!loading && !error && filteredData.length === 0 && users.length > 0" class="empty-state">
            <span>{{ $t('employer.selectListTaf.noSearchResults') || 'نتیجه‌ای یافت نشد' }}</span>
        </div>

        <!-- No Data -->
        <div v-if="!loading && !error && users.length === 0" class="empty-state">
            <span>{{ $t('employer.selectListTaf.noRecords') || 'هیچ کاربری یافت نشد' }}</span>
        </div>

        <!-- Success Toast -->
        <div v-if="showToast" class="toast-message" :class="toastType">
            <span>{{ toastMessage }}</span>
            <button class="toast-close" @click="showToast = false">×</button>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { adminCompanyService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'
import { useRouter } from 'vue-router'

export default {
    name: 'SelectListTafView',
    components: {
        Navbar
    },
    setup() {
        const authStore = useAuthStore()
        const router = useRouter()
        
        // State
        const users = ref([])
        const loading = ref(false)
        const error = ref(null)
        const searchQuery = ref('')
        const currentPage = ref(1)
        const itemsPerPage = 10
        const submitting = ref(false)
        const showToast = ref(false)
        const toastMessage = ref('')
        const toastType = ref('success')
        const bulkRoleSelection = ref([])

        const isAuthenticated = computed(() => authStore.isAuthenticated)

        // Available roles with Persian labels
        const availableRoles = [
            { value: 'customer', label: 'مشتری' },
            { value: 'employer', label: 'کارفرما' },
            { value: 'admin', label: 'ادمین' },
            { value: 'store', label: 'فروشگاه' },
            { value: 'accountant', label: 'حسابدار' },
            { value: 'office', label: 'اتوماسیون اداری' },
            { value: 'employee', label: 'کارمند' },
        ]

        // Role mapping for Persian display
        const roleMapping = {
            'customer': 'مشتری',
            'employer': 'کارفرما',
            'admin': 'ادمین',
            'store': 'فروشگاه',
            'accountant': 'حسابدار',
            'office': 'اتوماسیون اداری',
            'employee': 'کارمند',
        }

        // Get Persian role name
        const getPersianRole = (role) => {
            if (!role) return 'نقش نامشخص'
            
            // Handle comma-separated roles
            const roles = role.split(',').map(r => r.trim())
            const persianRoles = roles.map(r => roleMapping[r] || r)
            return persianRoles.join('، ')
        }

        // Check if role is selected for a user
        const isRoleSelected = (user, roleValue) => {
            if (!user.selectedRoles) return false
            return user.selectedRoles.includes(roleValue)
        }

        // Handle role change
        const onRoleChange = (user, roleValue) => {
            if (!user.selectedRoles) {
                user.selectedRoles = []
            }
            
            const index = user.selectedRoles.indexOf(roleValue)
            if (index > -1) {
                user.selectedRoles.splice(index, 1)
            } else {
                user.selectedRoles.push(roleValue)
            }
            
            // Mark user as changed
            user._changed = true
            user._selected = true // Auto-select changed users
            
            console.log(`User ${user.Id_subcode} roles:`, user.selectedRoles)
        }

        // Toggle user selection
        const toggleUserSelection = (user) => {
            user._selected = !user._selected
        }

        // Toggle select all
        const toggleSelectAll = () => {
            const allSelected = filteredData.value.every(user => user._selected)
            filteredData.value.forEach(user => {
                user._selected = !allSelected
            })
        }

        // Check if all users are selected
        const allSelected = computed(() => {
            return filteredData.value.length > 0 && filteredData.value.every(user => user._selected)
        })

        // Count selected users
        const selectedUsersCount = computed(() => {
            return users.value.filter(user => user._selected).length
        })

        // Apply bulk roles
        const applyBulkRoles = () => {
            if (bulkRoleSelection.value.length === 0) return
            
            const selectedUsers = users.value.filter(user => user._selected)
            selectedUsers.forEach(user => {
                user.selectedRoles = [...bulkRoleSelection.value]
                user._changed = true
            })
            
            showToastMessage(
                `نقش‌ها به ${selectedUsers.length} کاربر اعمال شد`,
                'success'
            )
            
            // Clear bulk selection
            bulkRoleSelection.value = []
        }

        // Initialize selectedRoles for each user
        const initializeUserRoles = (user) => {
            if (!user.selectedRoles) {
                const roles = user.Role ? user.Role.split(',').map(r => r.trim()) : []
                user.selectedRoles = roles.filter(r => roleMapping[r])
                user._originalRoles = [...user.selectedRoles]
                user._changed = false
                user._selected = false
            }
        }

        // Get role class for card header
        const getRoleClass = (role) => {
            if (!role) return 'role-unknown'
            
            const roleLower = role.toLowerCase()
            if (roleLower.includes('customer')) return 'role-customer'
            if (roleLower.includes('employer')) return 'role-employer'
            if (roleLower.includes('admin')) return 'role-admin'
            if (roleLower.includes('store')) return 'role-store'
            if (roleLower.includes('accountant')) return 'role-accountant'
            return 'role-unknown'
        }

        // Get role badge class
        const getRoleBadgeClass = (role) => {
            if (!role) return 'badge-unknown'
            
            const roleLower = role.toLowerCase()
            if (roleLower.includes('customer')) return 'badge-customer'
            if (roleLower.includes('employer')) return 'badge-employer'
            if (roleLower.includes('admin')) return 'badge-admin'
            if (roleLower.includes('store')) return 'badge-store'
            if (roleLower.includes('accountant')) return 'badge-accountant'
            return 'badge-unknown'
        }

        // Check if any changes exist
        const hasChanges = computed(() => {
            return users.value.some(user => user._changed)
        })

        // Count changed users
        const changedUsersCount = computed(() => {
            return users.value.filter(user => user._changed).length
        })

        // Reset all changes
        const resetChanges = () => {
            users.value.forEach(user => {
                if (user._changed) {
                    user.selectedRoles = [...user._originalRoles]
                    user._changed = false
                }
            })
            showToastMessage('تغییرات لغو شد', 'success')
        }

        // Submit role changes
        const submitRoleChanges = async () => {
            const changedUsers = users.value.filter(user => user._changed)
            if (changedUsers.length === 0) return

            submitting.value = true
            error.value = null

            try {
                const payload = changedUsers.map(user => ({
                    Id_subcode: user.Id_subcode,
                    roles: user.selectedRoles || []
                }))

                console.log('Submitting role changes:', payload)
                
                // Call API to update roles
                const response = await adminCompanyService.updateUserRoles(payload)
                
                if (response.data.success) {
                    // Mark changes as saved
                    changedUsers.forEach(user => {
                        user._originalRoles = [...user.selectedRoles]
                        user._changed = false
                    })
                    
                    showToastMessage(
                        response.data.message || 'تغییرات با موفقیت ذخیره شد',
                        'success'
                    )
                    
                    // Refresh user list after a short delay
                    setTimeout(async () => {
                        await fetchUserList()
                    }, 1000)
                } else {
                    showToastMessage(
                        response.data.message || 'خطا در ذخیره تغییرات',
                        'error'
                    )
                    
                    // Show failed updates if any
                    if (response.data.failed_updates && response.data.failed_updates.length > 0) {
                        console.error('Failed updates:', response.data.failed_updates)
                    }
                }
            } catch (err) {
                console.error('Error submitting role changes:', err)
                showToastMessage(
                    err.response?.data?.detail || 'خطا در ارتباط با سرور',
                    'error'
                )
            } finally {
                submitting.value = false
            }
        }

        // Show toast message
        const showToastMessage = (message, type = 'success') => {
            toastMessage.value = message
            toastType.value = type
            showToast.value = true
            setTimeout(() => {
                showToast.value = false
            }, 5000)
        }

        // Fetch user list
        const fetchUserList = async () => {
            if (!authStore.user?.customerId) {
                console.error('No customer ID found')
                return
            }

            loading.value = true
            error.value = null

            try {
                console.log('🔧 Fetching user list...')
                const response = await adminCompanyService.getListTaf()
                
                if (response.data.success) {
                    users.value = response.data.data || []
                    // Initialize selected roles for each user
                    users.value.forEach(user => initializeUserRoles(user))
                    console.log('✅ Users loaded:', users.value.length, 'records')
                } else {
                    error.value = response.data.message || 'خطا در دریافت اطلاعات'
                    console.error('❌ Failed to load users:', error.value)
                }
            } catch (err) {
                console.error('❌ Error fetching user list:', err)
                error.value = err.response?.data?.detail || 'خطا در ارتباط با سرور'
            } finally {
                loading.value = false
            }
        }

        // Navigate to dashboard
        const navigateToDashboard = () => {
            router.push('/admin/dashboard')
        }

        // Filter data based on search query
        const filteredData = computed(() => {
            if (!searchQuery.value.trim()) {
                return users.value
            }
            
            const query = searchQuery.value.trim().toLowerCase()
            return users.value.filter(user => {
                const title = (user.title || '').toLowerCase()
                const phone = (user.Tel2 || '').toLowerCase()
                const role = (user.Role || '').toLowerCase()
                const id = String(user.Id_subcode || '')
                
                return title.includes(query) || 
                       phone.includes(query) || 
                       role.includes(query) || 
                       id.includes(query)
            })
        })

        // Paginated data
        const paginatedData = computed(() => {
            const startIndex = (currentPage.value - 1) * itemsPerPage
            const endIndex = startIndex + itemsPerPage
            return filteredData.value.slice(startIndex, endIndex)
        })

        // Total pages
        const totalPages = computed(() => {
            return Math.ceil(filteredData.value.length / itemsPerPage)
        })

        // Pagination methods
        const nextPage = () => {
            if (currentPage.value < totalPages.value) {
                currentPage.value++
            }
        }

        const prevPage = () => {
            if (currentPage.value > 1) {
                currentPage.value--
            }
        }

        // Reset to first page when search changes
        watch(searchQuery, () => {
            currentPage.value = 1
        })

        // Print functionality
        const printData = () => {
            const printWindow = window.open('', '_blank')
            const tableContent = generatePrintableTable()
            
            printWindow.document.write(`
                <html dir="rtl">
                    <head>
                        <title>لیست کاربران</title>
                        <style>
                            body { 
                                font-family: Tahoma, Arial, sans-serif; 
                                margin: 20px;
                                direction: rtl;
                            }
                            h1 { 
                                text-align: center; 
                                color: #333;
                                margin-bottom: 20px;
                            }
                            table {
                                width: 100%;
                                border-collapse: collapse;
                                margin-top: 10px;
                            }
                            th, td {
                                border: 1px solid #ddd;
                                padding: 8px;
                                text-align: right;
                            }
                            th {
                                background-color: #f5f5f5;
                                font-weight: bold;
                            }
                            .role-customer { color: #2196F3; }
                            .role-employer { color: #FF9800; }
                            .role-admin { color: #9C27B0; }
                            .role-store { color: #4CAF50; }
                            .role-accountant { color: #795548; }
                            @media print {
                                body { margin: 0; }
                                table { width: 100%; }
                            }
                        </style>
                    </head>
                    <body>
                        <h1>لیست کاربران</h1>
                        <div>تاریخ چاپ: ${new Date().toLocaleDateString('fa-IR')}</div>
                        <div>تعداد کل کاربران: ${filteredData.value.length}</div>
                        ${tableContent}
                    </body>
                </html>
            `)
            
            printWindow.document.close()
            printWindow.focus()
            setTimeout(() => {
                printWindow.print()
            }, 500)
        }

        const generatePrintableTable = () => {
            return `
                <table>
                    <thead>
                        <tr>
                            <th>شناسه</th>
                            <th>نام</th>
                            <th>تلفن</th>
                            <th>نقش</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${filteredData.value.map(user => `
                            <tr>
                                <td>${user.Id_subcode}</td>
                                <td>${user.title || '-'}</td>
                                <td>${user.Tel2 || '-'}</td>
                                <td class="role-${user.Role ? user.Role.toLowerCase().replace(', ', '-') : 'unknown'}">
                                    ${getPersianRole(user.Role)}
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `
        }

        onMounted(async () => {
            if (isAuthenticated.value) {
                await fetchUserList()
            }
        })

        return {
            users,
            filteredData,
            paginatedData,
            loading,
            error,
            searchQuery,
            currentPage,
            totalPages,
            isAuthenticated,
            availableRoles,
            hasChanges,
            changedUsersCount,
            submitting,
            showToast,
            toastMessage,
            toastType,
            bulkRoleSelection,
            allSelected,
            selectedUsersCount,
            navigateToDashboard,
            fetchUserList,
            nextPage,
            prevPage,
            printData,
            getRoleClass,
            getRoleBadgeClass,
            getPersianRole,
            isRoleSelected,
            onRoleChange,
            submitRoleChanges,
            showToastMessage,
            toggleUserSelection,
            toggleSelectAll,
            applyBulkRoles,
            resetChanges
        }
    }
}
</script>

<style scoped>
.bank-app {
    display: flex;
    flex-direction: column;
    margin-bottom: 45px;
    background-color: var(--bg);
    direction: rtl;
}

/* Section Header */
.section-header {
    display: flex;
    align-items: center;
    margin: 10px 15px 15px 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid var(--primary);
}

.section-title {
    font-size: 1.1rem;
    color: var(--heading);
    margin: 0;
}

/* Action Bar */
.action-bar {
    display: flex;
    gap: 10px;
    align-items: center;
    margin: 0 15px 20px 15px;
    flex-wrap: wrap;
    background: var(--card-bg);
    padding: 12px 16px;
    border-radius: 10px;
    box-shadow: 0 2px 8px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.print-btn {
    background: var(--header-1);
    color: white;
    border: none;
    padding: 10px 15px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.3s;
}

.print-btn:hover {
    background: var(--header-2);
}

.search-container {
    position: relative;
    flex: 1;
    min-width: 200px;
}

.search-input {
    width: 100%;
    padding: 10px 40px 10px 15px;
    border: 1px solid var(--card-border);
    border-radius: 8px;
    font-size: 14px;
    direction: rtl;
    transition: border-color 0.3s;
    background: var(--bg);
    color: var(--text);
}

.search-input::placeholder {
    color: var(--subtext);
}

.search-input:focus {
    outline: none;
    border-color: var(--primary);
}

.search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--subtext);
}

.total-count {
    font-size: 14px;
    color: var(--subtext);
    font-weight: 500;
    white-space: nowrap;
    padding: 5px 12px;
    background: var(--bg);
    border-radius: 6px;
}

/* Select All */
.select-all-container {
    display: flex;
    align-items: center;
}

.select-all-checkbox {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-size: 13px;
    color: var(--text);
    padding: 5px 10px;
    background: var(--bg);
    border-radius: 6px;
    border: 1px solid var(--card-border);
}

.select-all-checkbox input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--primary);
}

/* User Grid */
.user-section {
    margin: 0 15px 20px 15px;
}

.user-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
}

.user-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px var(--shadow-1);
    border: 1px solid var(--card-border);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.user-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px var(--shadow-2);
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--card-border);
}

.user-id {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--muted);
    font-weight: 500;
}

.user-select-checkbox {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--primary);
}

.user-role {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 12px;
    color: white;
}

.role-customer {
    background: var(--primary);
}

.role-employer {
    background: var(--orange-1);
}

.role-admin {
    background: var(--gradient-2);
}

.role-store {
    background: var(--green);
}

.role-accountant {
    background: #795548;
}

.role-unknown {
    background: var(--muted);
}

.card-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.user-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.info-label {
    font-size: 13px;
    color: var(--muted);
    font-weight: 500;
    min-width: 60px;
}

.info-value {
    font-size: 14px;
    color: var(--text);
    font-weight: 500;
}

.info-value.phone {
    direction: ltr;
    font-family: 'Courier New', monospace;
}

.role-row {
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px dashed var(--card-border);
}

.role-badge {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    color: white;
}

.badge-customer {
    background: var(--primary);
}

.badge-employer {
    background: var(--orange-1);
}

.badge-admin {
    background: var(--gradient-2);
}

.badge-store {
    background: var(--green);
}

.badge-accountant {
    background: #795548;
}

.badge-unknown {
    background: var(--muted);
}

/* Role Selection */
.role-selection-row {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 2px solid var(--ring);
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.role-checkboxes {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
}

.role-checkbox {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    cursor: pointer;
    font-size: 12px;
    padding: 2px 6px;
    background: var(--bg);
    border-radius: 4px;
    border: 1px solid var(--card-border);
    transition: all 0.2s;
    white-space: nowrap;
    color: var(--text);
}

.role-checkbox:hover {
    background: var(--ring);
}

.role-checkbox-selected {
    background: var(--ring);
    border-color: var(--primary);
}

.role-checkbox input[type="checkbox"] {
    margin: 0;
    width: 13px;
    height: 13px;
    cursor: pointer;
    flex-shrink: 0;
    accent-color: var(--primary);
}

.checkbox-label {
    font-size: 11px;
    color: var(--subtext);
}

/* Bulk Assign */
.bulk-assign-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    padding: 16px;
    background: var(--ring);
    border-radius: 10px;
    border: 2px solid var(--primary);
    flex-wrap: wrap;
}

.bulk-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--heading);
}

.bulk-role-checkboxes {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.bulk-role-checkbox {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    font-size: 13px;
    padding: 4px 10px;
    background: var(--card-bg);
    border-radius: 4px;
    border: 1px solid var(--card-border);
    color: var(--text);
}

.bulk-role-checkbox input[type="checkbox"] {
    margin: 0;
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: var(--primary);
}

.apply-bulk-btn {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
    border: none;
    padding: 8px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.3s;
}

.apply-bulk-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--ring);
}

.apply-bulk-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Submit Changes Button */
.submit-changes-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    margin-top: 20px;
    padding: 20px;
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.submit-left {
    display: flex;
    align-items: center;
    gap: 20px;
}

.submit-right {
    display: flex;
    align-items: center;
}

.submit-changes-btn {
    background: linear-gradient(135deg, var(--green) 0%, #2ecc71 100%);
    color: white;
    border: none;
    padding: 12px 32px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    gap: 10px;
}

.submit-changes-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.submit-changes-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.reset-changes-btn {
    background: var(--orange-1);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    gap: 8px;
}

.reset-changes-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(249, 115, 22, 0.4);
}

.reset-changes-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.changes-count {
    font-size: 14px;
    color: var(--orange-1);
    font-weight: 600;
}

.spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: var(--subtext);
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid var(--card-border);
    border-top: 4px solid var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 15px;
}

/* Error State */
.error-state {
    text-align: center;
    padding: 60px 20px;
    color: var(--orange-1);
}

.error-icon {
    font-size: 48px;
    margin-bottom: 15px;
}

.error-state h4 {
    margin: 0 0 10px 0;
    color: var(--orange-1);
}

.error-state p {
    margin: 0 0 20px 0;
    color: var(--subtext);
}

.retry-btn {
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: transform 0.2s;
}

.retry-btn:hover {
    transform: translateY(-2px);
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: var(--subtext);
    background: var(--card-bg);
    border-radius: 12px;
    border: 1px dashed var(--card-border);
    margin: 0 15px;
}

/* Pagination */
.pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
    margin-top: 10px;
}

.pagination-btn {
    padding: 8px 16px;
    border: 1px solid var(--card-border);
    background: var(--card-bg);
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    color: var(--text);
}

.pagination-btn:hover:not(:disabled) {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-size: 14px;
    color: var(--subtext);
}

/* Toast Message */
.toast-message {
    position: fixed;
    top: 80px;
    right: 20px;
    padding: 16px 24px;
    border-radius: 8px;
    color: white;
    font-size: 14px;
    font-weight: 500;
    box-shadow: 0 4px 12px var(--shadow-2);
    z-index: 9999;
    animation: slideIn 0.3s ease;
    display: flex;
    align-items: center;
    gap: 12px;
    max-width: 400px;
}

.toast-message.success {
    background: var(--green);
}

.toast-message.error {
    background: var(--orange-1);
}

.toast-close {
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    padding: 0 4px;
}

@keyframes slideIn {
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .user-grid {
        grid-template-columns: 1fr;
    }

    .action-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .search-container {
        min-width: 100%;
    }

    .total-count {
        text-align: center;
    }

    .pagination-controls {
        flex-wrap: wrap;
        gap: 10px;
    }

    .role-checkboxes {
        flex-wrap: wrap;
        gap: 4px;
    }

    .role-checkbox {
        font-size: 11px;
        padding: 2px 4px;
    }

    .role-selection-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }

    .submit-changes-container {
        flex-direction: column;
        gap: 12px;
    }

    .bulk-assign-container {
        flex-direction: column;
        align-items: stretch;
    }

    .toast-message {
        top: 70px;
        right: 10px;
        left: 10px;
        max-width: none;
    }
}

@media (min-width: 769px) and (max-width: 1024px) {
    .user-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (min-width: 1025px) {
    .user-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}
</style>